import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { Table } from '../../components/ui/Table';
import { TableActionButton, TableActions } from '../../components/ui';
import { Settings, Plus, X, ShieldOff, ShieldCheck, Trash2 } from 'lucide-react';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { apiFetch } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { useNotificationStore } from '@/stores/notificationStore';
import { useOrgStore } from '@/stores/orgStore';
import { t } from '@/lib/i18n';

export function OrgsList() {
  usePageHeader({ title: t('Admin Dashboard'), breadcrumbs: [] });

  const navigate = useNavigate();
  const { success, error: notifyError } = useNotificationStore();
  const setActiveOrgId = useOrgStore(s => s.setActiveOrgId);
  const [loading, setLoading] = React.useState(false);
  const [orgs, setOrgs] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [createForm, setCreateForm] = React.useState({ name: '', slug: '', ownerEmail: '' });
  const [creating, setCreating] = React.useState(false);

  const fetchOrgs = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/orgs');
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load');

      // Fetch member counts for each org
      const orgsWithCounts = await Promise.all(
        (json.data || []).map(async (org: any) => {
          try {
            const detailRes = await apiFetch(`/api/admin/orgs/${org.id}`);
            const detailJson = await detailRes.json();
            return {
              ...org,
              memberCount: detailJson.success ? detailJson.data.memberCount : 0
            };
          } catch {
            return { ...org, memberCount: 0 };
          }
        })
      );

      setOrgs(orgsWithCounts);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch('/api/admin/orgs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createForm.name.trim(),
          slug: createForm.slug.trim() || undefined,
          ownerEmail: createForm.ownerEmail.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to create organization');
      success(t('Organization created successfully'));
      setShowCreateModal(false);
      setCreateForm({ name: '', slug: '', ownerEmail: '' });
      fetchOrgs();
    } catch (e: any) {
      notifyError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (orgId: string, newStatus: 'active' | 'suspended') => {
    try {
      const res = await apiFetch(`/api/admin/orgs/${orgId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to update status');
      success(newStatus === 'active' ? t('Organization activated successfully') : t('Organization suspended successfully'));
      fetchOrgs();
    } catch (e: any) {
      notifyError(e.message);
    }
  };

  const handleDelete = async (orgId: string, orgName: string) => {
    if (!window.confirm(`${t('Are you sure you want to permanently delete this organization and all its data? This will remove the organization, all its members, users (who don\'t belong to other organizations), and all associated data. This action cannot be undone.')}\n\n${t('Organization')}: ${orgName}`)) return;
    try {
      const res = await apiFetch(`/api/admin/orgs/${orgId}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to delete organization');
      const usersRemoved = json.data?.deletedUsers ?? 0;
      success(`${t('Organization permanently deleted')}: ${orgName} (${usersRemoved} ${t('users removed')})`);
      fetchOrgs();
    } catch (e: any) {
      notifyError(e.message);
    }
  };

  const columns = [
    {
      key: 'name',
      title: t('Organization'),
      sortable: true,
      render: (value: string, record: any) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground">{record.slug}</div>
        </div>
      )
    },
    {
      key: 'status',
      title: t('Status'),
      sortable: true,
      render: (value: string) => {
        const variant = value === 'active' ? 'success' : value === 'suspended' ? 'error' : 'warning';
        return <Badge variant={variant}>{value}</Badge>;
      }
    },
    {
      key: 'memberCount',
      title: t('Members'),
      sortable: true,
      align: 'center' as const,
      render: (value: number) => (
        <span className="font-medium">{value}</span>
      )
    },
    {
      key: 'created_at',
      title: t('Created'),
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'share_config_with_admin',
      title: t('Sharing'),
      align: 'center' as const,
      render: (value: boolean) => (
        <Badge variant={value ? 'success' : 'secondary'}>{value ? t('Enabled') : t('Disabled')}</Badge>
      )
    },
    {
      key: 'actions',
      title: t('Actions'),
      align: 'right' as const,
      render: (_: any, record: any) => (
        <TableActions>
          {record.share_config_with_admin && (
            <TableActionButton
              icon={Settings}
              onClick={() => navigate(`/admin/orgs/${record.id}`)}
            >
              Settings
            </TableActionButton>
          )}
          {record.status === 'active' ? (
            <TableActionButton
              icon={ShieldOff}
              onClick={() => handleStatusChange(record.id, 'suspended')}
            >
              Suspend
            </TableActionButton>
          ) : record.status === 'suspended' ? (
            <TableActionButton
              icon={ShieldCheck}
              onClick={() => handleStatusChange(record.id, 'active')}
            >
              Activate
            </TableActionButton>
          ) : null}
          <TableActionButton
            icon={Trash2}
            variant="destructive"
            onClick={() => handleDelete(record.id, record.name)}
          >
            Delete
          </TableActionButton>
        </TableActions>
      )
    }
  ];

  const filters = [
    {
      key: 'status',
      label: t('Filter by Status'),
      options: [
        { label: t('Active'), value: 'active' },
        { label: t('Suspended'), value: 'suspended' },
        { label: t('Trialing'), value: 'trialing' }
      ]
    }
  ];

  return (
    <PageContainer>
      <div className="flex items-center justify-end mb-4 gap-2">
        <Button size="sm" onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Create Organization
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={orgs}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search organizations..."
        filters={filters}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50]
        }}
        emptyText="No organizations found."
      />

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Create Organization</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 text-muted-foreground hover:text-foreground rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Organization Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Corp"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-background border-border text-foreground"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Slug</label>
                <input
                  type="text"
                  value={createForm.slug}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="acme-corp (auto-generated if empty)"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-background border-border text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">URL-friendly identifier. Auto-generated from name if left empty.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Owner Email *</label>
                <input
                  type="email"
                  value={createForm.ownerEmail}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  placeholder="owner@example.com"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-background border-border text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">An invitation email will be sent to this person to complete registration.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={creating || !createForm.name.trim() || !createForm.ownerEmail.trim()}>
                {creating ? 'Creating...' : 'Create Organization'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
